import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Box,
  Button,
  TextField,
  IconButton,
  Switch,
  Typography,
  CircularProgress,
  Collapse,
  Chip,
  Tooltip,
  Autocomplete,
  Stack,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LinkIcon from '@mui/icons-material/Link';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSnackbar } from 'notistack';
import categoryService, { Category } from '../services/category.service';
import accountTypeService from '../services/accountType.service';
import IconPicker from '../components/common/IconPicker';
import { ConfirmDialog } from '../components/common';
import { findIconByName } from '../utils/categoryIconRegistry';

interface AccountTypeOption {
  id: string;
  name: string;
}

interface FormState {
  name: string;
  slug: string;
  description: string;
  icon: string;
  image: string;
  parent: string | null;
  order: number;
  isActive: boolean;
  metaTitle: string;
  metaDescription: string;
}

const emptyForm: FormState = {
  name: '',
  slug: '',
  description: '',
  icon: '',
  image: '',
  parent: null,
  order: 0,
  isActive: true,
  metaTitle: '',
  metaDescription: '',
};

const CategoryManagement: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // Data
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [accountTypes, setAccountTypes] = useState<AccountTypeOption[]>([]);

  // Tree expand state
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Form dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<Category | null>(null);

  // Attach AccountTypes dialog
  const [attachTarget, setAttachTarget] = useState<Category | null>(null);
  const [attachIds, setAttachIds] = useState<string[]>([]);
  const [attachLoading, setAttachLoading] = useState(false);
  const [accountTypeCategoryMap, setAccountTypeCategoryMap] = useState<Record<string, string[]>>({});

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [cats, ats] = await Promise.all([
        categoryService.list({ includeDeleted: false }),
        accountTypeService.getAccountTypes(1, 1000, ''),
      ]);
      setCategories(cats);

      // ats has shape { data: AccountType[] }
      const list = (ats as any)?.data || [];
      const opts: AccountTypeOption[] = list.map((at: any) => ({
        id: String(at._id || at.id),
        name: at.name,
      }));
      setAccountTypes(opts);

      const map: Record<string, string[]> = {};
      list.forEach((at: any) => {
        const id = String(at._id || at.id);
        const cats: string[] = (at.categoryIds || []).map((c: any) =>
          typeof c === 'string' ? c : String(c?._id || c?.id || c)
        );
        map[id] = cats;
      });
      setAccountTypeCategoryMap(map);
    } catch (e: any) {
      console.error(e);
      enqueueSnackbar('خطا در بارگذاری دسته‌بندی‌ها', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const { l1List, l2ByParent } = useMemo(() => {
    const l1 = categories.filter((c) => c.level === 1).sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
    const l2map = new Map<string, Category[]>();
    categories
      .filter((c) => c.level === 2 && c.parent)
      .forEach((c) => {
        const k = String(c.parent);
        const arr = l2map.get(k) || [];
        arr.push(c);
        l2map.set(k, arr);
      });
    Array.from(l2map.values()).forEach((arr: Category[]) => {
      arr.sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
    });
    return { l1List: l1, l2ByParent: l2map };
  }, [categories]);

  const accountTypesForCategory = useCallback(
    (categoryId: string): AccountTypeOption[] => {
      return accountTypes.filter((at) => (accountTypeCategoryMap[at.id] || []).includes(categoryId));
    },
    [accountTypes, accountTypeCategoryMap]
  );

  const toggleExpand = (id: string) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };

  const openCreate = (parent: Category | null) => {
    setEditing(null);
    setForm({
      ...emptyForm,
      parent: parent?._id || null,
      order: parent ? (l2ByParent.get(parent._id)?.length || 0) : l1List.length,
    });
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = (cat: Category) => {
    setEditing(cat);
    setForm({
      name: cat.name,
      slug: cat.slug,
      description: cat.description || '',
      icon: cat.icon || '',
      image: cat.image || '',
      parent: cat.parent || null,
      order: cat.order,
      isActive: cat.isActive,
      metaTitle: cat.metaTitle || '',
      metaDescription: cat.metaDescription || '',
    });
    setFormError('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setFormError('نام دسته‌بندی الزامی است');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        slug: form.slug.trim() || undefined,
        description: form.description.trim(),
        icon: form.icon.trim() || undefined,
        image: form.image.trim() || undefined,
        parent: form.parent,
        order: form.order,
        isActive: form.isActive,
        metaTitle: form.metaTitle.trim() || undefined,
        metaDescription: form.metaDescription.trim() || undefined,
      };
      if (editing) {
        await categoryService.update(editing._id, payload);
        enqueueSnackbar('دسته‌بندی بروزرسانی شد', { variant: 'success' });
      } else {
        await categoryService.create(payload);
        enqueueSnackbar('دسته‌بندی ساخته شد', { variant: 'success' });
      }
      closeDialog();
      await loadData();
    } catch (e: any) {
      setFormError(e?.response?.data?.message || 'خطا در ذخیره دسته‌بندی');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await categoryService.remove(deleteTarget._id);
      let msg = 'دسته‌بندی حذف شد';
      if (res.cascadedChildren) msg += ` (${res.cascadedChildren} زیرمجموعه نیز)`;
      if (res.detachedAccountTypes) msg += ` (${res.detachedAccountTypes} نوع‌محصول جداسازی شد)`;
      enqueueSnackbar(msg, { variant: 'success' });
      setDeleteTarget(null);
      await loadData();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'خطا در حذف', { variant: 'error' });
    }
  };

  const openAttach = (cat: Category) => {
    if (cat.level !== 2) return;
    setAttachTarget(cat);
    setAttachIds(accountTypesForCategory(cat._id).map((a) => a.id));
  };

  const closeAttach = () => {
    setAttachTarget(null);
    setAttachIds([]);
  };

  const handleAttachSave = async () => {
    if (!attachTarget) return;
    setAttachLoading(true);
    try {
      const res = await categoryService.setAccountTypes(attachTarget._id, attachIds);
      enqueueSnackbar(`اعمال شد (${res.added} اضافه، ${res.removed} حذف)`, { variant: 'success' });
      closeAttach();
      await loadData();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'خطا در ذخیره', { variant: 'error' });
    } finally {
      setAttachLoading(false);
    }
  };

  const renderL2Row = (l2: Category) => {
    const ats = accountTypesForCategory(l2._id);
    const Icon = findIconByName(l2.icon);
    return (
      <Box
        key={l2._id}
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 1,
          px: 2,
          mr: 4,
          borderBottom: '1px solid #f1f5f9',
          gap: 1,
        }}
      >
        {Icon ? (
          <Icon size={18} color="#64748b" />
        ) : (
          <Box sx={{ width: 8, height: 8, bgcolor: '#cbd5e1', borderRadius: '50%' }} />
        )}
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
            {l2.name} {!l2.isActive && <Chip size="small" label="غیرفعال" color="default" sx={{ ml: 1 }} />}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
            slug: {l2.slug} • ترتیب: {l2.order} • {ats.length} نوع‌محصول
          </Typography>
          {ats.length > 0 && (
            <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {ats.map((a) => (
                <Chip key={a.id} label={a.name} size="small" variant="outlined" />
              ))}
            </Box>
          )}
        </Box>
        <Tooltip title="اتصال نوع‌محصول‌ها">
          <IconButton size="small" onClick={() => openAttach(l2)} color="primary">
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="ویرایش">
          <IconButton size="small" onClick={() => openEdit(l2)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="حذف">
          <IconButton size="small" onClick={() => setDeleteTarget(l2)} color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const renderL1Row = (l1: Category) => {
    const isOpen = expanded.has(l1._id);
    const children = l2ByParent.get(l1._id) || [];
    const Icon = findIconByName(l1.icon);
    return (
      <Box key={l1._id} sx={{ borderBottom: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, gap: 1, bgcolor: isOpen ? '#f8fafc' : 'transparent' }}>
          <IconButton size="small" onClick={() => toggleExpand(l1._id)}>
            {isOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
          </IconButton>
          {Icon && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 1, bgcolor: '#FFF4F7', color: '#B25373' }}>
              <Icon size={18} />
            </Box>
          )}
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
              {l1.name}{' '}
              {!l1.isActive && <Chip size="small" label="غیرفعال" color="default" sx={{ ml: 1 }} />}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
              slug: {l1.slug} • ترتیب: {l1.order} • {children.length} زیرمجموعه
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => openCreate(l1)}
            sx={{ minWidth: 'auto' }}
          >
            افزودن زیرمجموعه
          </Button>
          <Tooltip title="ویرایش">
            <IconButton size="small" onClick={() => openEdit(l1)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="حذف">
            <IconButton size="small" onClick={() => setDeleteTarget(l1)} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <Box sx={{ bgcolor: '#fafbfc' }}>
            {children.length > 0 ? (
              children.map(renderL2Row)
            ) : (
              <Typography sx={{ p: 2, mr: 4, color: '#94a3b8', fontSize: '0.85rem' }}>
                هنوز زیرمجموعه‌ای ندارد. روی «افزودن زیرمجموعه» کلیک کنید.
              </Typography>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          مدیریت دسته‌بندی‌ها
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}>
            بروزرسانی
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openCreate(null)}>
            دسته‌بندی اصلی جدید
          </Button>
        </Stack>
      </Box>

      <Typography sx={{ color: '#64748b', mb: 2, fontSize: '0.85rem' }}>
        ساختار: دسته‌بندی اصلی (سطح 1) → زیرمجموعه (سطح 2) → نوع محصول‌ها (متصل به زیرمجموعه)
      </Typography>

      <Box sx={{ bgcolor: 'white', border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : l1List.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: '#94a3b8' }}>
            <Typography>دسته‌بندی‌ای وجود ندارد. اولین دسته اصلی را بسازید.</Typography>
          </Box>
        ) : (
          l1List.map(renderL1Row)
        )}
      </Box>

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editing
            ? `ویرایش «${editing.name}»`
            : form.parent
            ? 'افزودن زیرمجموعه'
            : 'افزودن دسته‌بندی اصلی'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {formError && (
            <Box sx={{ bgcolor: '#fee2e2', color: '#991b1b', p: 1, borderRadius: 1, fontSize: '0.85rem' }}>
              {formError}
            </Box>
          )}
          <TextField
            label="نام"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
            autoFocus
            required
          />
          <TextField
            label="Slug (لاتین، اختیاری — خودکار از نام ساخته می‌شود)"
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
            fullWidth
            placeholder="skin-care"
          />
          <TextField
            label="توضیحات"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            multiline
            rows={2}
            fullWidth
          />
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch' }}>
            <Box sx={{ flex: 1 }}>
              <IconPicker
                value={form.icon}
                onChange={(name) => setForm({ ...form, icon: name })}
              />
            </Box>
            <TextField
              label="ترتیب"
              type="number"
              value={form.order}
              onChange={(e) => setForm({ ...form, order: parseInt(e.target.value, 10) || 0 })}
              sx={{ width: 120, alignSelf: 'flex-end' }}
            />
          </Box>
          <TextField
            label="تصویر (URL)"
            value={form.image}
            onChange={(e) => setForm({ ...form, image: e.target.value })}
            fullWidth
            placeholder="https://cdn..."
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />
            <Typography>{form.isActive ? 'فعال' : 'غیرفعال'}</Typography>
          </Box>
          <Divider />
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>SEO (اختیاری)</Typography>
          <TextField
            label="Meta Title"
            value={form.metaTitle}
            onChange={(e) => setForm({ ...form, metaTitle: e.target.value })}
            fullWidth
          />
          <TextField
            label="Meta Description"
            value={form.metaDescription}
            onChange={(e) => setForm({ ...form, metaDescription: e.target.value })}
            multiline
            rows={2}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting}>انصراف</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <CircularProgress size={18} /> : 'ذخیره'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={`حذف «${deleteTarget?.name}»`}
        message={
          <span>
            آیا از حذف این دسته‌بندی مطمئنید؟
            {deleteTarget?.level === 1 && ' زیرمجموعه‌های آن نیز حذف می‌شوند و نوع‌محصول‌های مرتبط جداسازی می‌شوند.'}
          </span>
        }
        confirmText="حذف"
        cancelText="انصراف"
        confirmColor="error"
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />

      {/* Attach AccountTypes */}
      <Dialog open={Boolean(attachTarget)} onClose={closeAttach} maxWidth="sm" fullWidth>
        <DialogTitle>اتصال نوع‌محصول‌ها به «{attachTarget?.name}»</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.85rem', color: '#64748b', mb: 2 }}>
            هر نوع‌محصول می‌تواند در چند زیرمجموعه باشد.
          </Typography>
          <Autocomplete
            multiple
            options={accountTypes}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={accountTypes.filter((a) => attachIds.includes(a.id))}
            onChange={(_, newVal) => setAttachIds(newVal.map((v) => v.id))}
            renderInput={(params) => <TextField {...params} label="نوع‌محصول‌ها" />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAttach} disabled={attachLoading}>
            انصراف
          </Button>
          <Button variant="contained" onClick={handleAttachSave} disabled={attachLoading}>
            {attachLoading ? <CircularProgress size={18} /> : 'ذخیره'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoryManagement;
